import { supabase } from "@core/lib/supabase";
import { logger } from "@core/lib/logger";
import { ErrorFactory } from "@core/lib/error-handling";

interface DatePollEmailData {
  // ID of the persisted poll_responses row. The confirmation recipient is
  // derived server-side from this row (#810) — the client no longer chooses
  // the recipient address.
  responseId: string;
  selectedDates: Array<{
    date: string;
    slots?: string[];
  }>;
  respondentName?: string;
}

interface FormPollEmailData {
  responseId: string;
  responses: Array<{
    question: string;
    answer: string;
  }>;
  respondentName?: string;
}

/**
 * Confirmation emails now enqueued server-side by voter_confirmation_queue trigger.
 * Client call is a no-op; confirmation will be sent asynchronously via edge function drain.
 */
export async function sendDatePollConfirmationEmail(
  data: DatePollEmailData,
): Promise<{ success: boolean; error?: string }> {
  logger.info("Date poll confirmation queued server-side", "api", {
    responseId: data.responseId,
  });
  return { success: true };
}

/**
 * Confirmation emails now enqueued server-side by voter_confirmation_queue trigger.
 * Client call is a no-op; confirmation will be sent asynchronously via edge function drain.
 */
export async function sendFormPollConfirmationEmail(
  data: FormPollEmailData,
): Promise<{ success: boolean; error?: string }> {
  logger.info("Form poll confirmation queued server-side", "api", {
    responseId: data.responseId,
  });
  return { success: true };
}

/**
 * Vérifie si l'envoi d'email est activé pour un poll
 */
export function shouldSendConfirmationEmail(settings: {
  sendEmailCopy?: boolean;
  emailForCopy?: string;
}): boolean {
  return !!(settings?.sendEmailCopy && settings?.emailForCopy);
}

/**
 * Génère le HTML de l'email de confirmation pour un Form Poll
 */
function generateFormPollEmailHTML(data: {
  pollTitle: string;
  responses: Array<{ question: string; answer: string }>;
  respondentName?: string;
}): string {
  const respondentName = data.respondentName || "Anonyme";
  const responsesList = data.responses
    .map(
      (item) => `
    <div style="margin-bottom: 20px; padding-bottom: 20px; border-bottom: 1px solid #e0e0e0;">
      <p style="margin: 0 0 8px; color: #333; font-size: 16px; font-weight: 600;">
        ${escapeHtml(item.question)}
      </p>
      <p style="margin: 0; color: #666; font-size: 15px; line-height: 1.6;">
        ${escapeHtml(item.answer)}
      </p>
    </div>
  `,
    )
    .join("");

  return `
<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Vos réponses : ${escapeHtml(data.pollTitle)}</title>
</head>
<body style="margin: 0; padding: 0; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif; background-color: #f5f5f5;">
  <table width="100%" cellpadding="0" cellspacing="0" style="background-color: #f5f5f5; padding: 40px 20px;">
    <tr>
      <td align="center">
        <table width="600" cellpadding="0" cellspacing="0" style="background-color: #ffffff; border-radius: 8px; box-shadow: 0 2px 8px rgba(0,0,0,0.1);">
          <tr>
            <td style="padding: 40px 40px 20px; text-align: center; background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); border-radius: 8px 8px 0 0;">
              <h1 style="margin: 0; color: #ffffff; font-size: 28px; font-weight: 600;">DooQuest</h1>
              <p style="margin: 8px 0 0; color: #ffffff; font-size: 16px; opacity: 0.9;">Confirmation de réponse</p>
            </td>
          </tr>
          <tr>
            <td style="padding: 40px;">
              <h2 style="margin: 0 0 20px; color: #333; font-size: 24px; font-weight: 600;">
                ${escapeHtml(data.pollTitle)}
              </h2>
              <p style="margin: 0 0 24px; color: #666; font-size: 16px;">Bonjour ${escapeHtml(respondentName)},</p>
              <p style="margin: 0 0 24px; color: #666; font-size: 16px;">
                Merci d'avoir répondu au formulaire ! Voici un récapitulatif de vos réponses :
              </p>
              <div style="background-color: #f8f9fa; padding: 24px; border-radius: 4px; margin-bottom: 24px;">
                ${responsesList}
              </div>
            </td>
          </tr>
        </table>
      </td>
    </tr>
  </table>
</body>
</html>
  `.trim();
}

/**
 * Échappe les caractères HTML pour éviter les injections XSS
 */
function escapeHtml(text: string): string {
  if (typeof text !== "string") {
    return String(text);
  }
  const map: Record<string, string> = {
    "&": "&amp;",
    "<": "&lt;",
    ">": "&gt;",
    '"': "&quot;",
    "'": "&#039;",
  };
  return text.replace(/[&<>"']/g, (m) => map[m]);
}

/**
 * Formate la réponse selon le type de question
 */
function formatAnswer(
  value: unknown,
  question: {
    kind?: string;
    options?: Array<{ id: string; label: string }>;
    matrixRows?: Array<{ id: string; label: string }>;
    matrixColumns?: Array<{ id: string; label: string }>;
    ratingScale?: number;
  },
): string {
  // Si la question n'est pas trouvée, retourner la valeur telle quelle
  if (!question) {
    return String(value);
  }

  if (typeof value === "string") {
    // Pour les questions single, trouver le label de l'option
    if (question.kind === "single" && question.options) {
      const option = question.options?.find(
        (opt: { id: string; label: string }) => opt.id === value,
      );
      return option?.label || value;
    }
    return value;
  } else if (Array.isArray(value)) {
    // Pour les questions multiple, trouver les labels des options
    if (question.kind === "multiple" && question.options) {
      return value
        .map((optId: string) => {
          const option = question.options?.find(
            (opt: { id: string; label: string }) => opt.id === optId,
          );
          return option?.label || optId;
        })
        .join(", ");
    }
    return value.join(", ");
  } else if (typeof value === "object" && value !== null) {
    // Pour les questions matrix
    if (question.kind === "matrix") {
      const entries = Object.entries(value);
      return entries
        .map(([rowId, colValue]) => {
          const row = question.matrixRows?.find(
            (r: { id: string; label: string }) => r.id === rowId,
          );
          const rowLabel = row?.label || rowId;

          if (Array.isArray(colValue)) {
            // Multiple choice matrix
            const colLabels = colValue
              .map((colId: string) => {
                const col = question.matrixColumns?.find(
                  (c: { id: string; label: string }) => c.id === colId,
                );
                return col?.label || colId;
              })
              .join(", ");
            return `${rowLabel}: ${colLabels}`;
          } else {
            // Single choice matrix
            const col = question.matrixColumns?.find(
              (c: { id: string; label: string }) => c.id === colValue,
            );
            const colLabel = col?.label || colValue;
            return `${rowLabel}: ${colLabel}`;
          }
        })
        .join("; ");
    }
    return JSON.stringify(value);
  } else if (typeof value === "number") {
    // Pour les questions rating et NPS
    if (question.kind === "rating" && question.ratingScale) {
      return `${value}/${question.ratingScale}`;
    } else if (question.kind === "nps") {
      return `${value}/10`;
    }
    return String(value);
  }
  return String(value);
}

/**
 * Fonction wrapper pour FormPollVote (compatibilité avec l'ancien code)
 */
export async function sendVoteConfirmationEmail(params: {
  poll: {
    id: string;
    title: string;
    settings?: { sendEmailCopy?: boolean; emailForCopy?: string };
  };
  response: {
    id?: string;
    items?: Array<{ questionId: string; value: unknown }>;
    respondentEmail?: string;
    respondentName?: string;
  };
  questions: Array<{
    id: string;
    title: string;
    kind?: string;
    options?: Array<{ id: string; label: string }>;
    matrixRows?: Array<{ id: string; label: string }>;
    matrixColumns?: Array<{ id: string; label: string }>;
    ratingScale?: number;
  }>;
}): Promise<void> {
  const { poll, response, questions } = params;

  // Valider que l'email est présent
  if (!response.respondentEmail) {
    const metadata: Record<string, unknown> = {
      component: "EmailService",
      operation: "sendVoteConfirmationEmail",
    };
    const validationError = ErrorFactory.validation(
      "Email du votant manquant",
      "Email du votant manquant",
      metadata,
    );
    throw validationError;
  }

  // Construire le tableau de réponses avec formatage approprié
  const items = response.items || [];
  const formattedResponses: Array<{ question: string; answer: string }> = [];

  for (const item of items) {
    const question = questions.find(
      (q: {
        id: string;
        title: string;
        kind?: string;
        options?: Array<{ id: string; label: string }>;
        matrixRows?: Array<{ id: string; label: string }>;
        matrixColumns?: Array<{ id: string; label: string }>;
        ratingScale?: number;
      }) => q.id === item.questionId,
    );
    const questionTitle = question?.title || "Question";
    const answerText = formatAnswer(item.value || {}, question);

    formattedResponses.push({
      question: questionTitle,
      answer: answerText,
    });
  }

  // Générer le HTML de l'email
  const html = generateFormPollEmailHTML({
    pollTitle: poll.title,
    responses: formattedResponses,
    respondentName: response.respondentName,
  });

  // En mode test/dev, logger à la console
  const isTestOrDev =
    (typeof process !== "undefined" && process.env?.NODE_ENV === "test") ||
    !import.meta.env?.PROD;
  if (isTestOrDev) {
    console.log("📧 Email à envoyer:", {
      to: response.respondentEmail,
      subject: `Vos réponses : ${poll.title}`,
      html,
    });
  }

  // En production, appeler la fonction principale.
  // responseId est requis : le recipient est dérivé server-side depuis cette
  // ligne poll_responses (#810). Sans id persisté, on ne peut rien envoyer.
  if (import.meta.env?.PROD) {
    if (!response.id) {
      logger.error(
        "Cannot send vote confirmation: response.id missing",
        "api",
        { pollId: poll.id },
      );
      return;
    }
    await sendFormPollConfirmationEmail({
      responseId: response.id,
      responses: formattedResponses,
      respondentName: response.respondentName,
    });
  }
}

interface InvitationData {
  pollId: string;
  // pollTitle/pollUrl/senderName are kept for backward compatibility with
  // callers but are no longer sent: the Edge function derives poll metadata
  // server-side from pollId after verifying ownership (#810).
  pollTitle?: string;
  pollUrl?: string;
  recipientEmails: string[];
  senderName?: string;
}

/**
 * Envoie des emails d'invitation pour un Date Poll.
 *
 * Sécurité (#810) : un seul appel avec tous les destinataires ; l'Edge
 * function vérifie que l'appelant (JWT) est bien le créateur du poll et lit
 * titre/slug côté serveur. Le destinataire arbitraire est légitime ici (le
 * créateur invite qui il veut), contrairement aux emails de confirmation.
 */
export async function sendDatePollInvitation({
  pollId,
  recipientEmails,
}: InvitationData): Promise<{
  success: boolean;
  sentCount: number;
  error?: string;
}> {
  try {
    logger.info("Sending date poll invitations", "api", {
      pollId,
      recipientCount: recipientEmails.length,
    });

    // Call proxy (verify_jwt), which validates ownership + re-invokes with service-role
    const { error } = await supabase.functions.invoke(
      "send-poll-invitation-email-proxy",
      {
        body: {
          pollId,
          recipients: recipientEmails,
        },
      },
    );

    if (error) {
      logger.error("Failed to send invitations", "api", { error, pollId });
      return {
        success: false,
        sentCount: 0,
        error: error.message,
      };
    }

    return {
      success: true,
      sentCount: recipientEmails.length,
    };
  } catch (error) {
    logger.error("Failed to send invitations", "api", { error });
    return {
      success: false,
      sentCount: 0,
      error: error instanceof Error ? error.message : "Unknown error",
    };
  }
}
